import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Alert
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../../contexts/AuthContext';
import { followUser, unfollowUser, getFollowStatus, getUserFollowers, getUserFollowing, UserInfo } from '../../services/FollowService';
import { getUserOpinions } from '../../services/OpinionService';
import { Opinion } from '../../interface/Opinion';
import OpinionCard from '../dashboard/OpinionCard';
import UserAvatar from '../common/UserAvatar';
import { formatDistanceToNow } from 'date-fns';
import HttpClient from '../../services/HttpClient';
import { OPINION_SERVICE_HOST, OPINIONS_API_PREFIX } from '../../services/RestConstants';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followStatus, setFollowStatus] = useState<{ followersCount: number; followingCount: number } | null>(null);
  const [followers, setFollowers] = useState<UserInfo[]>([]);
  const [followingUsers, setFollowingUsers] = useState<UserInfo[]>([]);
  const [opinions, setOpinions] = useState<Opinion[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loadingFollow, setLoadingFollow] = useState(false);
  const [error, setError] = useState('');
  const [profileUser, setProfileUser] = useState<{ username: string; profilePicture?: string } | null>(null);

  const isOwnProfile = currentUser?._id === userId;

  useEffect(() => {
    if (userId) {
      loadProfileData();
    }
  }, [userId]);

  const loadProfileData = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError('');

      // Load user info
      try {
        const userResponse = await HttpClient.GET({
          BASE_URL: OPINION_SERVICE_HOST,
          END_POINT: `${OPINIONS_API_PREFIX}/users/${userId}`,
        });
        if (userResponse.data.success) {
          setProfileUser({
            username: userResponse.data.data.user.username,
            profilePicture: userResponse.data.data.user.profilePicture
          });
        }
      } catch (err) {
        console.error('Error loading user info:', err);
      }

      // Load follow status if user is logged in
      if (currentUser?._id && !isOwnProfile) {
        try {
          const status = await getFollowStatus(userId);
          setFollowing(status.isFollowing);
          setFollowStatus({
            followersCount: status.followersCount,
            followingCount: status.followingCount
          });
        } catch (err) {
          console.error('Error loading follow status:', err);
        }
      }

      // Load user opinions
      const opinionsData = await getUserOpinions(userId, 20);
      setOpinions(opinionsData.opinions);
      
      // If we don't have user info yet, get it from the first opinion
      if (!profileUser && opinionsData.opinions.length > 0) {
        setProfileUser({
          username: opinionsData.opinions[0].username
        });
      }

      // Load followers and following if viewing own profile or if following status is available
      if (isOwnProfile || followStatus) {
        try {
          const [followersData, followingData] = await Promise.all([
            getUserFollowers(userId, 1, 20),
            getUserFollowing(userId, 1, 20)
          ]);
          setFollowers(followersData.data.followers);
          setFollowingUsers(followingData.data.following);
          
          if (isOwnProfile) {
            setFollowStatus({
              followersCount: followersData.data.pagination.totalCount,
              followingCount: followingData.data.pagination.totalCount
            });
          }
        } catch (err) {
          console.error('Error loading followers/following:', err);
        }
      }
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!userId || !currentUser?._id) {
      navigate('/login');
      return;
    }

    try {
      setLoadingFollow(true);
      if (following) {
        await unfollowUser(userId);
        setFollowing(false);
        if (followStatus) {
          setFollowStatus({
            ...followStatus,
            followersCount: Math.max(0, followStatus.followersCount - 1)
          });
        }
      } else {
        await followUser(userId);
        setFollowing(true);
        if (followStatus) {
          setFollowStatus({
            ...followStatus,
            followersCount: followStatus.followersCount + 1
          });
        }
      }
    } catch (err: any) {
      console.error('Error following/unfollowing:', err);
      alert(err.response?.data?.error || 'Failed to update follow status');
    } finally {
      setLoadingFollow(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <UserAvatar
                  profileIcon={profileUser?.profilePicture}
                  username={profileUser?.username || userId || ''}
                  size={80}
                  sx={{ bgcolor: 'primary.main' }}
                />
                <Box>
                  <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
                    {profileUser?.username || (userId ? `User ${userId.substring(0, 8)}` : 'User')}
                  </Typography>
                  {followStatus && (
                    <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                      <Button
                        variant="text"
                        startIcon={<PeopleIcon />}
                        onClick={() => navigate(`/profile/${userId}/followers`)}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': {
                            color: 'primary.main',
                            background: 'transparent'
                          }
                        }}
                      >
                        <strong>{followStatus.followersCount}</strong> Followers
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <strong>{followStatus.followingCount}</strong> Following
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {!isOwnProfile && currentUser?._id && (
                <Button
                  variant={following ? 'outlined' : 'contained'}
                  onClick={handleFollow}
                  disabled={loadingFollow}
                  startIcon={loadingFollow ? <CircularProgress size={16} /> : <PersonIcon />}
                >
                  {following ? 'Unfollow' : 'Follow'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
              <Tab label={`Opinions (${opinions.length})`} />
              {isOwnProfile && <Tab label={`Followers (${followStatus?.followersCount || 0})`} />}
              {isOwnProfile && <Tab label={`Following (${followStatus?.followingCount || 0})`} />}
            </Tabs>
          </Box>

          <CardContent>
            <TabPanel value={tabValue} index={0}>
              {opinions.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="text.secondary">
                    No opinions yet
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {opinions.map((opinion) => (
                    <OpinionCard
                      key={opinion._id}
                      opinion={opinion}
                      onVote={async () => {}}
                      voting={false}
                    />
                  ))}
                </Stack>
              )}
            </TabPanel>

            {isOwnProfile && (
              <>
                <TabPanel value={tabValue} index={1}>
                  {followers.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body1" color="text.secondary">
                        No followers yet
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {followers.map((user) => (
                        <Grid item xs={12} sm={6} md={4} key={user._id}>
                          <Card
                            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                            onClick={() => navigate(`/profile/${user._id}`)}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" gap={2}>
                                <UserAvatar
                                  profileIcon={user.profilePicture}
                                  username={user.username}
                                  size={48}
                                />
                                <Box>
                                  <Typography variant="body1" fontWeight={600}>
                                    {user.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Joined {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  {followingUsers.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body1" color="text.secondary">
                        Not following anyone yet
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {followingUsers.map((user) => (
                        <Grid item xs={12} sm={6} md={4} key={user._id}>
                          <Card
                            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                            onClick={() => navigate(`/profile/${user._id}`)}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" gap={2}>
                                <UserAvatar
                                  profileIcon={user.profilePicture}
                                  username={user.username}
                                  size={48}
                                />
                                <Box>
                                  <Typography variant="body1" fontWeight={600}>
                                    {user.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Joined {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </TabPanel>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default UserProfile;
